import React from 'react';
import { motion } from 'framer-motion';
// Icons now represent features of Amama Bypass
import { DollarSign, Shield, TrendingUp, Zap, Users, Scale } from 'lucide-react';

const platformServices = [
    {
        title: "Capital Type & Terms",
        description: "Amama Bypass offers founders flexible, non-dilutive financing—primarily Revenue-Based Financing (RBF) and bridge loans. Terms are transparent and founder-friendly.",
        icon: <DollarSign className="w-7 h-7 text-white" />,
        iconBg: "bg-blue-500"
    },
    {
        title: "Equity Retention Maximized",
        description: "Designed for scaling milestones and CapEx needs (e.g., compute, major hiring) where maximizing equity retention is crucial. No board seat or voting rights required.",
        icon: <Shield className="w-7 h-7 text-white" />,
        iconBg: "bg-purple-500"
    },
    {
        title: "Revenue-Aligned Repayment",
        description: "RBF repayment is tied directly to monthly revenue, providing a safety net during slower growth periods. Terms are customized to your specific revenue model and margin profile.",
        icon: <TrendingUp className="w-7 h-7 text-white" />,
        iconBg: "bg-teal-500"
    },
    {
        title: "Rapid Deployment",
        description: "Streamlined due diligence and quick fund deployment—often faster than traditional equity rounds—to meet immediate operational or GTM needs across EU–GCC–India.",
        icon: <Zap className="w-7 h-7 text-white" />,
        iconBg: "bg-yellow-500"
    },
    {
        title: "Portfolio Synergy",
        description: "Exclusively available to Amama Ventures portfolio companies, enabling integrated capital planning alongside follow-on equity rounds and platform support.",
        icon: <Users className="w-7 h-7 text-white" />,
        iconBg: "bg-red-500"
    },
    {
        title: "Global Compliance Scale",
        description: "Capital is deployed with consideration for cross-border tax and compliance, assisting with entity-level structuring in Estonia, Dubai, or Mumbai.",
        icon: <Scale className="w-7 h-7 text-white" />,
        iconBg: "bg-green-500"
    }
];

const Platform = () => {
    // --- UPDATED INTRO TEXT ---
    const introText = "Amama Bypass offers a core competitive advantage: integrated, non-dilutive capital solutions to maximize your runway and optimize your cap table during periods of critical growth.";

    return (
        <motion.div
            className="bg-background py-12 md:py-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
        >
            <div className="container mx-auto px-6">

                {/* --- Section Header: Focuses on Amama Bypass --- */}
                <div className="max-w-6xl mx-auto mb-16 text-center flex flex-col items-center">
                    <motion.h1
                        className="text-4xl md:text-5xl font-bold text-foreground mb-6 font-serif"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.2 }}
                    >
                        Amama Bypass: Non-Dilutive Capital
                    </motion.h1>

                    <motion.p
                        className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.4 }}
                    >
                        {introText}
                    </motion.p>
                </div>
                {/* --- End Section Header --- */}


                {/* --- Card Grid: Now detailing Bypass features --- */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
                    {platformServices.map((service, index) => (
                        <motion.div
                            key={service.title}
                            className="bg-card p-8 rounded-lg shadow-medium transition-all duration-500 border border-border hover:border-secondary/50 hover:shadow-strong group"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
                            viewport={{ once: true, amount: 0.2 }}
                            whileHover={{ y: -5, transition: { duration: 0.2 } }}
                        >
                            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${service.iconBg} mb-6`}>
                                {service.icon}
                            </div>

                            <h3 className="text-2xl font-bold text-card-foreground group-hover:text-primary mb-4 transition-colors duration-500">
                                {service.title}
                            </h3>

                            <p className="text-base text-muted-foreground leading-relaxed">{service.description}</p>

                            <div className="mt-6 h-1 w-0 bg-gradient-accent group-hover:w-full transition-all duration-500 rounded-full"></div>
                        </motion.div>
                    ))}
                </div>

                {/* Removed the separate Amama Bypass section as its content is now in the cards. */}
            </div>
        </motion.div>
    );
};

export default Platform;